import { useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { SafeDialog } from "components/Dialog";
import { parseVirtualEventId } from "encamp-shared/src/tasks/expandSeries";
import { gql } from "generated-graphql";
import { EventMutationScope, Frequency, TaskStatus } from "generated-graphql/graphql";
import { useDeleteDocumentMutation } from "hooks/documents";
import { useTenant } from "hooks/useTenant";
import invariant from "invariant";
import { useCallback, useMemo, useState } from "react";
import { v4 } from "uuid";

import { useTaskSchema } from "./createTaskSchema";
import useDeleteTaskMutation from "./hooks/useDeleteTask";
import { useUpdateTaskMutation } from "./hooks/useUpdateTask";
import RecurrenceScopeRadioDialog, {
  RecurrenceScopeRadioDialogProps,
} from "./RecurrenceScopeRadioDialog";
import { TASK_LIST_REFETCH_QUERIES } from "./schema";
import { TaskDialog } from "./TaskDialog";
import { TaskFormType } from "./types";

const GET_VIRTUAL_EVENT = gql(`
  query getVirtualEvent($taskId: ID!, $deadline: DateTime!) {
      virtualEvent(taskId: $taskId, deadline: $deadline) {
        id
        taskId
        seriesId
        title
        description
        deadline
        status
        taskStatus
        completedAt
        completionTiming
        completionDeltaDays
        reminderDays
        frequency
        interval
        assignee {
          id
          email
          person {
            first
            last
          }
        }
        subtasks {
          id
          title
          completedAt
        }
        facilities {
          id
          archivedAt
        }
        watchers {
          id
          email
          person {
            first
            last
          }
        }
        documents {
          id
          fileExtension
          title
          documentType
        }
        tags {
          id
          name
        }
        links
        comments {
          id
          reportId
          userId
          workflowId
          activityId
          submissionId
          taskId
          taskSeriesId
          taskDeadline
          comment
          isSystemMessage
          createdAt
          user {
            id
            email
            person {
              first
              last
            }
          }
        }
      }
  }
`);

interface EditTaskDialogProps {
  virtualEventId: string;
  onClose: () => void;
}

export const EditTaskDialog: React.FC<EditTaskDialogProps> = ({ virtualEventId, onClose }) => {
  const alerts = useAlerts();
  const { tenantId, loading: isTenantLoading } = useTenant();

  const [deleteTaskState, setDeleteTaskState] = useState<{
    open: boolean;
    recurring: boolean;
  }>({
    open: false,
    recurring: false,
  });

  const [mutationScopeDialogState, setOpenMutationScopeDialogState] = useState<{
    open: boolean;
    onSubmit: (scope: EventMutationScope) => void;
    hideOptions: EventMutationScope[];
    action: RecurrenceScopeRadioDialogProps["action"] | null;
  }>({
    open: false,
    onSubmit: () => {},
    hideOptions: [],
    action: null,
  });

  const schema = useTaskSchema();

  const { taskId, deadline } = parseVirtualEventId(virtualEventId);
  const { data: virtualEventQuery, loading: isVirtualEventLoading } = useQuery(GET_VIRTUAL_EVENT, {
    variables: {
      taskId,
      deadline,
    },
    skip: !taskId || !deadline || (deadline && isNaN(deadline.getTime())),
    fetchPolicy: "network-only",
  });

  const [updateTask, { loading: isUpdateTaskLoading }] = useUpdateTaskMutation(
    virtualEventQuery?.virtualEvent ?? null,
    tenantId ?? ""
  );

  const [deleteDocument] = useDeleteDocumentMutation({
    onError: (error) => {
      alerts.error("Failed to delete document");
    },
  });

  const handleClose = useCallback(
    (values: TaskFormType) => {
      // delete any documents that we may have created, as they were not meant to be persisted
      values.documents
        ?.filter(
          (document) =>
            !virtualEventQuery?.virtualEvent?.documents?.map((x) => x.id).includes(document.id)
        )
        .forEach((document) => {
          if (document.id) {
            deleteDocument({ variables: { id: document.id } });
          }
        });
      onClose();
    },
    [deleteDocument, onClose, virtualEventQuery?.virtualEvent?.documents]
  );

  const handleSave = useCallback(
    (data: TaskFormType, eventMutationScope: EventMutationScope) => {
      invariant(tenantId, "tenantId is required");
      updateTask({
        variables: {
          taskData: data,
          scope: eventMutationScope,
        },
        onCompleted: () => {
          alerts.success("Task updated");
          onClose();
        },
        onError: () => {
          alerts.error("Failed to update task");
        },
      });
    },
    [alerts, onClose, tenantId, updateTask]
  );

  const initialValues: TaskFormType = useMemo(() => {
    const virtualEvent = virtualEventQuery?.virtualEvent;
    return {
      title: virtualEvent?.title ?? "",
      assignee: virtualEvent?.assignee ?? null,
      description: virtualEvent?.description ?? "",
      frequency: virtualEvent?.frequency ?? Frequency.Once,
      interval: virtualEvent?.interval ?? 1,
      taskStatus: virtualEvent?.taskStatus ?? TaskStatus.Open,
      reminderDays:
        virtualEvent?.reminderDays.map((reminder) => ({
          days: reminder,
          id: v4(),
        })) ?? [],
      deadline: virtualEvent?.deadline,
      tags: virtualEvent?.tags ?? [],
      links:
        virtualEvent?.links?.map((link) => ({
          url: link,
          id: v4(),
        })) ?? [],
      subtasks:
        virtualEvent?.subtasks?.map((subtask) => ({
          title: subtask.title,
          completed: subtask.completedAt !== null,
          id: subtask.id,
        })) ?? [],
      facilityIds: virtualEvent?.facilities?.map((facility) => facility.id) ?? [],
      watchers: virtualEvent?.watchers ?? [],
      documents: virtualEvent?.documents ?? [],
      completedOn: virtualEvent?.completedAt,
      completionTiming: virtualEvent?.completionTiming ?? null,
      completionDeltaDays: virtualEvent?.completionDeltaDays ?? null,
      isSuggested: false,
    };
  }, [virtualEventQuery?.virtualEvent]);

  const onDialogSubmitted = useCallback(
    (data: TaskFormType, isCompletionOnly: boolean, isFrequencyChanged: boolean) => {
      if (virtualEventQuery?.virtualEvent?.frequency === Frequency.Once || isCompletionOnly) {
        handleSave(data, EventMutationScope.This);
      } else {
        setOpenMutationScopeDialogState({
          open: true,
          onSubmit: (scope) => handleSave(data, scope),
          hideOptions: isFrequencyChanged ? [EventMutationScope.This] : [],
          action: "Edit",
        });
      }
    },
    [virtualEventQuery?.virtualEvent?.frequency, handleSave, setOpenMutationScopeDialogState]
  );

  const resetMutationScopeDialog = useCallback(
    () =>
      setOpenMutationScopeDialogState({
        open: false,
        onSubmit: () => {},
        hideOptions: [],
        action: null,
      }),
    []
  );

  const resetDeleteTaskState = useCallback(() => {
    setDeleteTaskState({ open: false, recurring: false });
    resetMutationScopeDialog();
  }, [resetMutationScopeDialog]);

  const taskFacilities = virtualEventQuery?.virtualEvent?.facilities ?? [];
  const isReadOnly = isVirtualEventLoading || taskFacilities.some((f) => f.archivedAt != null);

  const [deleteTask] = useDeleteTaskMutation();

  const onDeleteTask = useCallback(
    (scope: EventMutationScope) => {
      deleteTask({
        variables: {
          input: {
            taskId,
            date: initialValues.deadline,
            eventMutationScope: scope,
          },
        },
        onCompleted: () => {
          alerts.success(`Event deleted`);
          onClose();
        },
        onError: (error) => {
          alerts.error(`Error deleting event: ${error.message}`);
        },
        refetchQueries: TASK_LIST_REFETCH_QUERIES,
      });
    },
    [deleteTask, initialValues.deadline, alerts, onClose, taskId]
  );
  if (!isVirtualEventLoading && !virtualEventQuery?.virtualEvent) {
    return (
      <SafeDialog open={true} onClose={onClose}>
        <DialogTitle>Event not found</DialogTitle>
        <DialogContent>
          <Typography>
            The event you are looking for may have been moved or deleted. You can close this message
            and return to the task list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </SafeDialog>
    );
  }

  return (
    <>
      <TaskDialog
        validationResolver={yupResolver(schema) as any}
        initialValues={initialValues}
        onSubmit={onDialogSubmitted}
        onClose={handleClose}
        isSubmitting={isUpdateTaskLoading}
        isLoading={isVirtualEventLoading || isTenantLoading}
        open={!!virtualEventId}
        cancelButtonText="Close"
        submitButtonText="Update Task"
        submitButtonHelperText={(isDirty) =>
          isDirty ? "Save task updates" : "Make a change to enable update"
        }
        virtualEventId={virtualEventQuery?.virtualEvent?.id ?? undefined}
        comments={virtualEventQuery?.virtualEvent?.comments ?? []}
        isReadOnly={isReadOnly}
        onDeleteTask={
          isReadOnly
            ? undefined
            : () => {
                const isRecurring = initialValues.frequency !== Frequency.Once;
                setDeleteTaskState({ open: true, recurring: isRecurring });
                if (isRecurring) {
                  setOpenMutationScopeDialogState({
                    open: true,
                    onSubmit: (scope) => onDeleteTask(scope),
                    hideOptions: [],
                    action: "Delete",
                  });
                }
              }
        }
      />
      {deleteTaskState.open && !deleteTaskState.recurring && (
        <ConfirmDialog
          open={deleteTaskState.open}
          onClose={resetDeleteTaskState}
          onConfirm={() => onDeleteTask(EventMutationScope.This)}
          header="Are you sure you want to permanently delete this task?"
          resources={[
            {
              resourceType: "Task",
              resourceName: initialValues.title,
            },
          ]}
        />
      )}
      {mutationScopeDialogState.open && mutationScopeDialogState.action && (
        <RecurrenceScopeRadioDialog
          {...mutationScopeDialogState}
          action={mutationScopeDialogState.action}
          onClose={resetMutationScopeDialog}
        />
      )}
    </>
  );
};
